import "@/assets/css/responsive.css";
import PageLoader from "@/components/loader/PageLoader";
import { useAxiosInstance } from "@/utils/useAxiosInstance";
import { faCalendarAlt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toZonedTime, formatInTimeZone } from "date-fns-tz";
import { endOfDay, startOfDay, addDays, parse } from "date-fns";
import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import AppointmentDetailsCard from "@/components/management/components/appointment/AppointmentDetails";
import { toast } from "sonner";
import { Appointment, Employee } from "@/utils/types";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Button, Checkbox } from "@headlessui/react";

interface OperatingHour {
  id: number;
  day: string;
  open_time: string;
  close_time: string;
}

interface CalendarStatus {
  connected: boolean;
  connected_at?: string;
  last_updated?: string;
  email?: string;
  calendar_id?: string;
  calendar_ids?: string[];
}

const formatTo12Hour = (hours: number, minutes: number) => {
  const period = hours >= 12 ? "PM" : "AM";
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  return `${displayHours}:${minutes.toString().padStart(2, "0")} ${period}`;
};

const generateTimeSlots = (operatingHours: OperatingHour[] | null) => {
  if (!operatingHours || operatingHours.length === 0) {
    return [];
  }

  try {
    let allTimeSlots: {
      time: string;
      showLabel: boolean;
      isBreak?: boolean;
    }[] = [];

    const sortedHours = [...operatingHours].sort((a, b) => {
      const timeA = parse(a.open_time.substring(0, 5), "HH:mm", new Date());
      const timeB = parse(b.open_time.substring(0, 5), "HH:mm", new Date());
      return timeA.getTime() - timeB.getTime();
    });

    sortedHours.forEach((period, index) => {
      if (index > 0) {
        const prevCloseTime = parse(
          sortedHours[index - 1].close_time.substring(0, 5),
          "HH:mm",
          new Date(),
        );
        const currentOpenTime = parse(
          period.open_time.substring(0, 5),
          "HH:mm",
          new Date(),
        );

        allTimeSlots.push({
          time: `Break (${formatTo12Hour(prevCloseTime.getHours(), prevCloseTime.getMinutes())} - ${formatTo12Hour(currentOpenTime.getHours(), currentOpenTime.getMinutes())})`,
          showLabel: true,
          isBreak: true,
        });
      }

      const openTimeStr = period.open_time.substring(0, 5);
      const closeTimeStr = period.close_time.substring(0, 5);

      const openTime = parse(openTimeStr, "HH:mm", new Date());
      const closeTime = parse(closeTimeStr, "HH:mm", new Date());

      let startMinutes = openTime.getHours() * 60 + openTime.getMinutes();
      let endMinutes = closeTime.getHours() * 60 + closeTime.getMinutes();

      startMinutes = Math.ceil(startMinutes / 15) * 15;

      if (endMinutes % 15 !== 0) {
        endMinutes = Math.ceil(endMinutes / 15) * 15;
      }

      if (closeTimeStr === "23:59") {
        endMinutes = 24 * 60;
      }

      let totalMinutes;
      if (
        endMinutes <= startMinutes &&
        !openTimeStr.startsWith("00:") &&
        !closeTimeStr.startsWith("23:")
      ) {
        totalMinutes = 24 * 60 - startMinutes + endMinutes;
      } else {
        totalMinutes = endMinutes - startMinutes;
      }

      const periodSlots = Array.from({
        length: Math.ceil(totalMinutes / 15),
      }).map((_, index) => {
        const totalMinutes = startMinutes + index * 15;
        const adjustedMinutes =
          totalMinutes >= 24 * 60 ? totalMinutes - 24 * 60 : totalMinutes;

        const hours = Math.floor(adjustedMinutes / 60);
        const minutes = adjustedMinutes % 60;

        if (totalMinutes === 24 * 60) {
          return {
            time: "12:00 AM",
            showLabel: true,
          };
        }

        return {
          time: formatTo12Hour(hours, minutes),
          showLabel:
            minutes === 0 || minutes === 15 || minutes === 30 || minutes === 45,
        };
      });

      allTimeSlots = [...allTimeSlots, ...periodSlots];
    });

    return allTimeSlots;
  } catch (error) {
    return [];
  }
};

const AppointmentTable: React.FC = () => {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [selectedBranch, setSelectedBranch] = useState<number | null>(null);
  const [selectedAppointment, setSelectedAppointment] =
    useState<Appointment | null>(null);
  const [axiosInstance, isFetching] = useAxiosInstance();
  const [branchTimezone, setBranchTimezone] = useState<string>("UTC");
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [operatingHours, setOperatingHours] = useState<OperatingHour[]>([]);
  const [currentDayOperatingHours, setCurrentDayOperatingHours] =
    useState<OperatingHour | null>(null);
  const [showOptions, setShowOptions] = useState<"Timeline" | "Calendar">(
    "Timeline",
  );
  const [calendarStatus, setCalendarStatus] = useState<CalendarStatus | null>(
    null,
  );
  const [calendarMode, setCalendarMode] = useState<"MONTH" | "WEEK" | "AGENDA">(
    "WEEK",
  );
  const [isCalendarLoading, setIsCalendarLoading] = useState(true);
  const [selectedEmployees, setSelectedEmployees] = useState<Employee[]>([]);
  const calendarIframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    const storedData = localStorage.getItem("user_data");
    if (storedData) {
      const parsedData = JSON.parse(storedData);
      const { selectedBranch } = parsedData;
      setSelectedBranch(selectedBranch.id);
      setBranchTimezone(selectedBranch.timezone || "UTC");
    }
  }, []);

  useEffect(() => {
    const fetchBranchData = async () => {
      if (!axiosInstance || !selectedBranch) return;

      try {
        const storedData = localStorage.getItem("user_data");
        const userData = storedData ? JSON.parse(storedData) : null;
        const businessName = userData?.owner?.business_name;

        const response = await axiosInstance.get(
          `api/v1/branch/${selectedBranch}/?business_name=${businessName}`,
        );

        if (response.data.operating_hours) {
          setOperatingHours(response.data.operating_hours);
        }
      } catch (error) {
        console.error("Error fetching branch data:", error);
        toast.error("Failed to fetch branch operating hours");
      }
    };

    fetchBranchData();
  }, [axiosInstance, selectedBranch]);

  useEffect(() => {
    if (selectedDate && operatingHours.length > 0) {
      const zonedDate = toZonedTime(selectedDate, branchTimezone);
      const dayName = zonedDate.toLocaleDateString("en-US", {
        weekday: "long",
      });

      const dayOperatingHours = operatingHours.filter(
        (hours) => hours.day === dayName,
      );
      setCurrentDayOperatingHours(
        dayOperatingHours.length > 0 ? dayOperatingHours[0] : null,
      );
    } else {
      console.log("Cannot set operating hours:", {
        hasSelectedDate: !!selectedDate,
        operatingHoursLength: operatingHours.length,
      });
    }
  }, [selectedDate, operatingHours, branchTimezone]);

  const formatToUTC = (
    date: Date | null,
    isEndOfDay: boolean = false,
  ): string => {
    if (!date || !branchTimezone) {
      return "";
    }
    const startOfDayDate = startOfDay(date);

    const adjustDate = new Date(startOfDayDate.getTime());

    const formattedDate = adjustDate.toISOString().slice(0, 19);
    console.log("formattedDate", formattedDate);

    return formattedDate;
  };

  useEffect(() => {
    const fetchEmployees = async () => {
      if (!axiosInstance || !selectedBranch) return;

      try {
        const response = await axiosInstance.get(
          `/api/v1/employee?branch=${selectedBranch}`,
        );
        if (response.data.results) {
          setEmployees(response.data.results);
        }
        // Add all employees with calendar IDs to selectedEmployees
        const employeesWithCalendarIds = response.data.results.filter(
          (emp: Employee) => emp.calendar_id,
        );
        setSelectedEmployees(employeesWithCalendarIds);
      } catch (error) {
        console.error("Error fetching employees:", error);
        toast.error("Failed to fetch employees");
      }
    };

    fetchEmployees();
  }, [axiosInstance, selectedBranch]);

  // Function to fetch appointments that can be called both on mount and by interval
  const fetchAppointments = async () => {
    if (!axiosInstance || !selectedBranch || !selectedDate) {
      return;
    }

    try {
      setLoading(true);
      const start = formatToUTC(selectedDate);
      const end = formatToUTC(addDays(selectedDate, 1));

      const response = await axiosInstance.get(
        `api/v1/appointment/?branch=${selectedBranch}&date_after=${start}&date_before=${end}`,
      );

      console.log("Appointments received:", response.data.results);
      setAppointments(response.data.results);
      setError(null);
    } catch (error) {
      console.error("Error fetching appointments:", error);
      setError("Failed to fetch appointments");
      toast.error("Failed to fetch appointments");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAppointments();

    const intervalId = setInterval(
      () => {
        fetchAppointments();
      },
      5 * 60 * 1000,
    );

    return () => {
      clearInterval(intervalId);
    };
  }, [axiosInstance, selectedBranch, selectedDate]);

  const handleAppointmentClick = (appointment: Appointment) => {
    if (appointment.appointment_source === "Google Calendar") {
      toast.info("To view the appointment, please go to the Calendar view");
      setSelectedAppointment(appointment);
    } else {
      setSelectedAppointment(appointment);
    }
  };

  const handleStatusChange = async (status: string) => {
    if (selectedAppointment) {
      try {
        await axiosInstance.post(
          `api/v1/appointment/${selectedAppointment.id}/update_appointment_status/`,
          {
            status: status,
          },
        );

        setAppointments((prevAppointments) =>
          prevAppointments.map((appointment) =>
            appointment.id === selectedAppointment.id
              ? { ...appointment, status: status }
              : appointment,
          ),
        );

        toast.success(`Appointment status updated to ${status}`);

        setSelectedAppointment(null);
      } catch (error) {
        console.error("Error updating appointment status:", error);
        toast.error("Failed to update appointment status");
      }
    }
  };

  const getAppointmentStyle = (appointment: Appointment) => {
    if (!currentDayOperatingHours) return {};

    const date = new Date(appointment.appointment_time);
    const zonedDate = toZonedTime(date, branchTimezone);
    const hours = zonedDate.getHours();
    const minutes = zonedDate.getMinutes();
    const totalMinutes = hours * 60 + minutes;

    const dayName = zonedDate.toLocaleDateString("en-US", { weekday: "long" });
    const dayOperatingHours = operatingHours.filter(
      (hours) => hours.day === dayName,
    );

    if (dayOperatingHours.length === 0) return { display: "none" };

    const sortedOperatingHours = [...dayOperatingHours].sort((a, b) => {
      const openTimeA = parse(a.open_time, "HH:mm:ss", new Date());
      const openTimeB = parse(b.open_time, "HH:mm:ss", new Date());
      return openTimeA.getTime() - openTimeB.getTime();
    });

    const appointmentPeriod = sortedOperatingHours.find((period) => {
      const openTime = parse(period.open_time, "HH:mm:ss", new Date());
      const closeTime = parse(period.close_time, "HH:mm:ss", new Date());
      const openMinutes = openTime.getHours() * 60 + openTime.getMinutes();
      const closeMinutes = closeTime.getHours() * 60 + closeTime.getMinutes();

      return totalMinutes >= openMinutes && totalMinutes <= closeMinutes;
    });

    if (!appointmentPeriod) return { display: "none" };

    const timeSlots = generateTimeSlots(sortedOperatingHours);

    let slotIndex = -1;
    let currentMinutes = 0;
    let previousEndMinutes = 0;

    for (let i = 0; i < timeSlots.length; i++) {
      const slot = timeSlots[i];

      if (slot.isBreak) {
        continue;
      }

      const [hourStr, minuteStr, period] = slot.time
        .replace(/\s+/g, " ")
        .split(/[:\s]/);
      let slotHour = parseInt(hourStr);
      const slotMinute = parseInt(minuteStr);

      if (period === "PM" && slotHour < 12) {
        slotHour += 12;
      } else if (period === "AM" && slotHour === 12) {
        slotHour = 0;
      }

      const slotTotalMinutes = slotHour * 60 + slotMinute;

      if (
        totalMinutes >= slotTotalMinutes &&
        (i === timeSlots.length - 1 || totalMinutes < slotTotalMinutes + 15)
      ) {
        slotIndex = i;
        currentMinutes = slotTotalMinutes;
        break;
      }

      previousEndMinutes = slotTotalMinutes;
    }

    if (slotIndex === -1) return { display: "none" };

    const offsetMinutes = totalMinutes - currentMinutes;
    const offsetPixels = (offsetMinutes / 15) * 50;

    const topPosition = slotIndex * 50 + offsetPixels;
    const duration = parseInt(appointment.duration) || 30;
    const height = Math.max(duration / 15, 1) * 50 - 2;

    return {
      position: "absolute" as const,
      top: `${topPosition}px`,
      height: `${height}px`,
      width: "95%",
      left: "2.5%",
      borderRadius: "4px",
      overflow: "hidden",
      cursor: "pointer",
      zIndex: 10,
    };
  };

  const timeSlots = generateTimeSlots(
    selectedDate && operatingHours.length > 0
      ? operatingHours.filter(
          (hours) =>
            hours.day ===
            selectedDate.toLocaleDateString("en-US", { weekday: "long" }),
        )
      : null,
  );
  const selectedDayName = selectedDate?.toLocaleDateString("en-US", {
    weekday: "long",
  });

  useEffect(() => {
    const fetchCalendarStatus = async () => {
      if (!axiosInstance || !selectedBranch) return;

      try {
        setIsCalendarLoading(true);
        const response = await axiosInstance.get(
          `/api/v1/calendar/status/?branch_id=${selectedBranch}`,
        );
        setCalendarStatus(response.data);
      } catch (error) {
        console.error("Error fetching calendar status:", error);
      } finally {
        setIsCalendarLoading(false);
      }
    };

    fetchCalendarStatus();
  }, [axiosInstance, selectedBranch]);

  // Function to handle employee selection
  const handleEmployeeSelect = async (employee: Employee) => {
    if (employee.calendar_id) {
      setSelectedEmployees((prev) => {
        const isSelected = prev.some((emp) => emp.id === employee.id);
        if (isSelected) {
          // Remove employee if already selected
          return prev.filter((emp) => emp.id !== employee.id);
        } else {
          // Add employee if not selected
          return [...prev, employee];
        }
      });

      // Update calendar status with all selected calendar IDs
      setCalendarStatus((prev) => {
        if (!prev) return null;
        const isSelected = selectedEmployees.some(
          (emp) => emp.id === employee.id,
        );
        const calendarIds = selectedEmployees
          .filter((emp) => emp.id !== employee.id) // Remove current employee if already selected
          .map((emp) => emp.calendar_id)
          .filter((id): id is string => !!id); // Filter out any undefined/null values

        if (!isSelected && employee.calendar_id) {
          // Add current employee's calendar ID if not already selected
          calendarIds.push(employee.calendar_id);
        }

        return {
          ...prev,
          calendar_ids: calendarIds,
          calendar_id: calendarIds.join(","), // Join multiple calendar IDs for the iframe
        };
      });
    }
  };

  // Filter employees with calendar IDs
  const employeesWithCalendars = employees.filter((emp) => emp.calendar_id);

  // Add useEffect for calendar refresh
  useEffect(() => {
    if (showOptions === "Calendar" && calendarStatus?.connected) {
      const refreshInterval = setInterval(
        () => {
          if (calendarIframeRef.current) {
            setIsCalendarLoading(true);
            calendarIframeRef.current.src = calendarIframeRef.current.src + ""; // prevents eslint from screaming
          }
        },
        5 * 60 * 1000,
      ); // 5 minutes

      return () => clearInterval(refreshInterval);
    }
  }, [showOptions, calendarStatus?.connected]);

  return (
    <PageLoader isFetching={isFetching}>
      <div className="container mx-auto md:px-4">
        <div className="mb-6">
          <div className="flex flex-col justify-between gap-2 md:flex-row md:items-center">
            <div className="relative inline-block">
              {showOptions === "Timeline" && (
                <>
                  <ReactDatePicker
                    selected={selectedDate}
                    onChange={(date: Date | null) => setSelectedDate(date)}
                    dateFormat="MM/dd/yyyy"
                    className="z-50 w-48 rounded-md border border-gray-300 bg-white px-3 py-2 pl-10 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholderText={t(
                      "back.management.appointment.selectDate",
                    )}
                    popperClassName="z-50"
                    onFocus={(e: React.FocusEvent<HTMLInputElement>) =>
                      (e.target.readOnly = true)
                    }
                  />
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <FontAwesomeIcon
                      icon={faCalendarAlt}
                      className="text-gray-400"
                    />
                  </div>
                </>
              )}
            </div>
            <div className="flex w-fit items-center overflow-hidden rounded-lg bg-white p-0.5 shadow-md">
              <Button
                className={`px-5 py-2 text-sm font-medium transition-all duration-300 ease-in-out ${
                  showOptions === "Timeline"
                    ? "rounded-md bg-red-500 text-white shadow-sm"
                    : "rounded-md bg-transparent text-gray-700 hover:bg-gray-100"
                }`}
                onClick={() => setShowOptions("Timeline")}
              >
                Timeline
              </Button>
              <div className="mx-1 h-6 w-0.5 bg-gray-200"></div>
              <Button
                className={`px-5 py-2 text-sm font-medium transition-all duration-300 ease-in-out ${
                  showOptions === "Calendar"
                    ? "rounded-md bg-red-500 text-white shadow-sm"
                    : "rounded-md bg-transparent text-gray-700 hover:bg-gray-100"
                }`}
                onClick={() => setShowOptions("Calendar")}
              >
                Calendar
              </Button>
            </div>
          </div>
        </div>

        {showOptions === "Timeline" ? (
          <div className="relative rounded-lg border border-gray-200 shadow">
            <div
              className="relative overflow-auto"
              style={{
                height: "calc(100vh - 250px)",
              }}
            >
              {!currentDayOperatingHours && selectedDate && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-900/50">
                  <div className="rounded-lg bg-white p-6 text-center shadow-lg">
                    <h3 className="text-xl font-semibold text-gray-900">
                      Branch is Closed
                    </h3>
                    <p className="mt-2 text-gray-600">
                      The branch is not operating on {selectedDayName}
                    </p>
                  </div>
                </div>
              )}
              <div className="relative flex">
                <div
                  className="sticky left-0 z-40 w-[100px] flex-shrink-0 bg-white"
                  style={{ height: "fit-content" }}
                >
                  <div className="sticky left-0 top-0 z-40 h-16 border-b border-r border-gray-200 bg-gray-50 p-2">
                    <div className="text-xs font-medium text-gray-500">
                      Time
                    </div>
                  </div>
                  <div className="relative">
                    {timeSlots.map((timeSlot, index) => (
                      <div
                        key={`${timeSlot.time}-${index}`}
                        className={`relative ${
                          timeSlot.isBreak
                            ? "flex h-[50px] items-center justify-center border-b border-r border-gray-200 bg-red-200"
                            : "h-[50px] border-b border-r border-gray-200 bg-gray-50 p-2"
                        }`}
                      >
                        {timeSlot.showLabel && (
                          <div
                            className={`text-xs ${timeSlot.isBreak ? "font-medium text-gray-700" : "text-gray-500"}`}
                          >
                            {timeSlot.time}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                <div
                  style={{
                    width: `${employees.length * 250}px`,
                  }}
                >
                  <div
                    className="grid"
                    style={{
                      gridTemplateColumns: `repeat(${employees.length}, 250px)`,
                      position: "sticky",
                      top: 0,
                      zIndex: 35,
                      backgroundColor: "#f9fafb",
                    }}
                  >
                    {employees.map((employee, index) => (
                      <div
                        key={employee.id}
                        className={`h-16 border-b border-r border-gray-200 bg-gray-50 p-2 ${
                          index === employees.length - 1 ? "rounded-tr-lg" : ""
                        }`}
                      >
                        <div className="text-sm font-medium">{`${employee.first_name} ${employee.last_name}`}</div>
                        <div className="text-xs text-gray-500">
                          {employee.job_title || "Staff"}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div
                    className="relative grid"
                    style={{
                      gridTemplateColumns: `repeat(${employees.length}, 250px)`,
                    }}
                  >
                    {employees.map((employee) => {
                      const employeeAppointments = appointments.filter(
                        (apt) => parseInt(apt.employee_id) === employee.id,
                      );
                      console.log(employeeAppointments);

                      return (
                        <div
                          key={employee.id}
                          className="relative"
                          style={{ height: `${timeSlots.length * 50}px` }}
                        >
                          {/* Render appointments */}
                          {employeeAppointments.map((appointment) => (
                            <div
                              key={appointment.id}
                              style={getAppointmentStyle(appointment)}
                              onClick={() =>
                                handleAppointmentClick(appointment)
                              }
                              className="mb-[2px] flex border border-gray-500 bg-white transition-colors duration-150 ease-in-out"
                            >
                              {appointment.appointment_source ===
                              "Google Calendar" ? (
                                <>
                                  <div className="mr-1 flex max-h-full min-h-full min-w-2 max-w-2 bg-blue-500"></div>
                                  <div className="flex flex-col px-1 py-2">
                                    <div className="text-sm text-gray-600">
                                      {appointment.notes}
                                    </div>
                                  </div>
                                </>
                              ) : (
                                <>
                                  <div
                                    className={`mr-1 flex max-h-full min-h-full min-w-2 max-w-2 ${
                                      appointment.order_payment_status ===
                                      "Paid"
                                        ? "bg-green-400"
                                        : "bg-yellow-300"
                                    }`}
                                  ></div>
                                  <div className="flex flex-col px-1 py-2">
                                    <div className="text font-medium">
                                      {appointment.service}
                                    </div>
                                    <div className="text-sm text-gray-600">
                                      {appointment.customer_name}
                                    </div>
                                    <div className="text-sm text-gray-500">
                                      {appointment.phone_number}
                                    </div>
                                  </div>
                                </>
                              )}
                            </div>
                          ))}

                          {/* Time slot grid lines */}
                          {timeSlots.map((timeSlot, slotIndex) => (
                            <div
                              key={`${employee.id}-${timeSlot.time}`}
                              className="absolute w-full border-b border-r border-gray-200"
                              style={{
                                top: `${slotIndex * 50}px`,
                                height: "50px",
                              }}
                            />
                          ))}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center text-center">
            {calendarStatus?.connected ? (
              <div className="w-full">
                <div className="mb-6 flex flex-col items-center justify-center gap-4">
                  <div className="flex w-full items-center justify-between gap-6">
                    {/* Calendar Mode Buttons */}
                    <div className="flex w-fit flex-wrap items-center justify-center gap-2">
                      <div className="flex w-fit items-center overflow-hidden rounded-lg bg-white p-0.5 shadow-sm">
                        <Button
                          className={`px-4 py-1.5 text-sm font-medium transition-all duration-300 ease-in-out ${
                            calendarMode === "WEEK"
                              ? "rounded-md bg-red-500 text-white shadow-sm"
                              : "rounded-md bg-transparent text-gray-700 hover:bg-gray-100"
                          }`}
                          onClick={() => setCalendarMode("WEEK")}
                        >
                          Week
                        </Button>
                        <div className="mx-1 h-5 w-0.5 bg-gray-200"></div>
                        <Button
                          className={`px-4 py-1.5 text-sm font-medium transition-all duration-300 ease-in-out ${
                            calendarMode === "MONTH"
                              ? "rounded-md bg-red-500 text-white shadow-sm"
                              : "rounded-md bg-transparent text-gray-700 hover:bg-gray-100"
                          }`}
                          onClick={() => setCalendarMode("MONTH")}
                        >
                          Month
                        </Button>
                        <div className="mx-1 h-5 w-0.5 bg-gray-200"></div>
                        <Button
                          className={`px-4 py-1.5 text-sm font-medium transition-all duration-300 ease-in-out ${
                            calendarMode === "AGENDA"
                              ? "rounded-md bg-red-500 text-white shadow-sm"
                              : "rounded-md bg-transparent text-gray-700 hover:bg-gray-100"
                          }`}
                          onClick={() => setCalendarMode("AGENDA")}
                        >
                          Agenda
                        </Button>
                      </div>
                    </div>
                  </div>

                  {employeesWithCalendars.length > 0 && (
                    <div className="flex w-full flex-col items-center justify-center gap-2 rounded-lg bg-white px-3 py-2 shadow-sm sm:w-auto">
                      <span className="text-sm text-gray-500">Calendars:</span>
                      <div className="flex w-full flex-wrap items-center justify-center gap-1.5">
                        {employeesWithCalendars.map((employee) => (
                          <Checkbox
                            key={employee.id}
                            checked={selectedEmployees.some(
                              (emp) => emp.id === employee.id,
                            )}
                            onChange={() => handleEmployeeSelect(employee)}
                            className={`flex cursor-pointer items-center gap-1.5 rounded-full px-3 py-1.5 text-sm transition-colors ${
                              selectedEmployees.some(
                                (emp) => emp.id === employee.id,
                              )
                                ? "bg-red-100 text-red-700"
                                : "hover:bg-gray-100"
                            }`}
                          >
                            <span className="h-3.5 w-3.5 rounded border border-gray-300 bg-white">
                              {selectedEmployees.some(
                                (emp) => emp.id === employee.id,
                              ) && (
                                <svg
                                  className="h-3.5 w-3.5 text-red-500"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M5 13l4 4L19 7"
                                  />
                                </svg>
                              )}
                            </span>
                            {employee.first_name} {employee.last_name}
                          </Checkbox>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Calendar Iframe */}
                <div className="relative h-[600px] w-full overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg">
                  {isCalendarLoading && (
                    <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/80">
                      <div className="flex flex-col items-center gap-3">
                        <div className="h-10 w-10 animate-spin rounded-full border-4 border-red-500 border-t-transparent"></div>
                        <p className="text-sm font-medium text-gray-600">
                          Loading calendar...
                        </p>
                      </div>
                    </div>
                  )}
                  {calendarStatus.calendar_id &&
                  selectedEmployees.length > 0 ? (
                    <iframe
                      ref={calendarIframeRef}
                      src={`https://calendar.google.com/calendar/embed?${selectedEmployees
                        .map(
                          (emp) =>
                            `src=${encodeURIComponent(emp.calendar_id || "")}`,
                        )
                        .filter((src) => !src.includes("undefined"))
                        .join(
                          "&",
                        )}&ctz=${encodeURIComponent(branchTimezone)}&mode=${calendarMode}&showNav=1&showTitle=0&showPrint=0&showTabs=0&showCalendars=0&showTz=0&height=600`}
                      style={{ border: 0 }}
                      width="100%"
                      height="100%"
                      title="Google Calendar"
                      onLoad={() => setIsCalendarLoading(false)}
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center">
                      <p className="text-sm text-gray-500">
                        No Employees with Google Calendar Connected
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="mt-4 flex flex-col items-center gap-2 rounded-lg bg-white p-6 shadow-sm">
                <FontAwesomeIcon
                  icon={faCalendarAlt}
                  className="text-2xl text-gray-400"
                />
                <p className="text-lg font-medium text-gray-500">
                  No Google Calendar connected
                </p>
                <p className="text-sm text-gray-400">
                  Connect your Google Calendar to view appointments here
                </p>
              </div>
            )}
          </div>
        )}

        {selectedAppointment && (
          <AppointmentDetailsCard
            isOpen={true}
            branchTimezone={branchTimezone}
            onClose={() => setSelectedAppointment(null)}
            appointment_id={selectedAppointment.id}
            onStatusChange={handleStatusChange}
          />
        )}
      </div>
    </PageLoader>
  );
};

export default AppointmentTable;
